<template>
  <!-- Desktop version -->
  <Motion
    as="div"
    :initial="{ opacity: 0, y: 20 }"
    :animate="{ opacity: 1, y: 0 }"
    :whileHover="{
      scale: 1.02,
      boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15)',
      filter: 'brightness(0.9)',
      transition: {
        duration: 0.3,
        ease: 'easeOut'
      }
    }"
    :transition="{
      duration: 0.5,
      backgroundColor: {
        delay: motionDelay / 1000,
      },
      opacity: {
        delay: motionDelay / 1000,
      },
      y: {
        delay: motionDelay / 1000,
      },
      ease: 'easeOut'
    }"
    class="hidden new_lg:flex flex-col h-full overflow-y-auto scrollbar-hide justify-between col-start-4 col-end-5 row-start-[4] row-end-[12] bg-[rgb(213,217,220)] rounded-[20px] font-basic cursor-pointer py-6 px-5 relative overflow-hidden"
  >
    <!-- 상단: Title -->
    <div>
      <p class="font-normal font-basic text-sm new_sm:text-lg new_lg:text-xl new_2xl:text-[26px] tracking-tight">
        LW는 신뢰높은 검증을 제공합니다.
      </p>
    </div>

    <!-- 중간: Reviews -->
    <div class="relative">
      <Transition name="fade" mode="out-in">
        <div :key="currentPage" class="flex flex-col gap-3">
          <div
            v-for="review in currentReviews"
            :key="review.id"
            class="flex items-start bg-[rgb(236,238,243)] rounded-xl px-4 new_2xl:py-4 py-2"
          >
            <div class="flex flex-col items-center justify-center py-2 new_2xl:py-4">
              <img
                :src="review.image"
                :alt="review.author"
                class="new_2xl:w-[66px] new_2xl:h-[66px] w-[48px] h-[48px] rounded-full object-fill my-auto"
              />
            </div>
            <div class="flex-1 w-full ml-2 new_xl:ml-4 new_2xl:ml-6 my-auto">
              <p class="text-xs new_2xl:text-lg text-black mb-1 ">{{ review.comment }}</p>
              <p class="text-xs text-black ml-auto text-right">{{ review.company }}, {{ review.author }}</p>
            </div>
          </div>
        </div>
      </Transition>
    </div>

    <!-- 하단: Subtitle -->
    <div>
      <p class="font-semibold font-paperlogy text-xl new_sm:text-3xl new_xl:text-4xl new_2xl:text-5xl whitespace-pre-wrap">
        Reviews
      </p>
    </div>
  </Motion>

  <!-- Mobile version -->
  <Motion
    tag="div"
    :initial="{ opacity: 0, y: 20 }"
    :animate="{ opacity: 1, y: 0 }"
    :whileHover="{
      scale: 1.02,
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
      filter: 'brightness(0.9)',
      transition: {
        duration: 0.3,
        ease: 'easeOut'
      }
    }"
    :transition="{
      duration: 0.5,
      backgroundColor: {
        delay: motionDelay / 1000,
      },
      opacity: {
        delay: motionDelay / 1000,
      },
      y: {
        delay: motionDelay / 1000,
      },
      ease: 'easeOut'
    }"
    class="new_sm:py-5 py-3 px-3 gap-2 rounded-[20px] relative cursor-pointer new_lg:hidden flex flex-col justify-between overflow-hidden bg-[rgb(213,217,220)]"
    @click="goToPage"
  >
    <div>
      <p class="font-semibold text-xl new_sm:text-3xl whitespace-pre-wrap">
        Reviews
      </p>
    </div>

    <!-- 중간: Reviews -->
    <div class="relative">
      <Transition name="fade" mode="out-in">
        <div :key="currentPage" class="flex flex-col gap-3">
          <div
            v-for="review in currentReviews"
            :key="review.id"
            class="flex items-center gap-2 bg-[rgb(236,238,243)] rounded-xl p-2"
          >
            <img
              :src="review.image"
              :alt="review.author"
              class="w-12 h-12 rounded-full object-cover flex-shrink-0 my-auto"
            />
            <div class="flex-1 w-full h-full flex flex-col justify-between ml-2">
              <p class="text-base text-black mb-1">{{ review.comment }}</p>
              <p class="text-xs text-black text-right">{{ review.company }}, {{ review.author }}</p>
            </div>
          </div>
        </div>
      </Transition>
    </div>

  </Motion>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useReviewData } from '@/composables/useReviewData'
import { Motion } from 'motion-v'

defineProps({
  motionDelay: {
    type: Number,
    default: 0
  }
})

const router = useRouter()

// Review data
const { reviews } = useReviewData()
const currentPage = ref(0)

// Compute items per page based on screen size using window width
const itemsPerPage = ref(window.innerWidth >= 1024 ? 2 : 1)

// Update items per page on resize
const updateItemsPerPage = () => {
  const newItemsPerPage = window.innerWidth >= 1024 ? 2 : 1
  if (newItemsPerPage !== itemsPerPage.value) {
    itemsPerPage.value = newItemsPerPage
    currentPage.value = 0 // Reset to first page when items per page changes
  }
}

// Compute current reviews to display
const currentReviews = computed(() => {
  const startIndex = currentPage.value * itemsPerPage.value
  return reviews.slice(startIndex, startIndex + itemsPerPage.value)
})

// Auto-rotate reviews
let intervalId = null

onMounted(() => {
  window.addEventListener('resize', updateItemsPerPage)

  intervalId = setInterval(() => {
    currentPage.value = (currentPage.value + 1) % Math.ceil(reviews.length / itemsPerPage.value)
  }, 5000) // Change every 5 seconds
})

onUnmounted(() => {
  window.removeEventListener('resize', updateItemsPerPage)

  if (intervalId) {
    clearInterval(intervalId)
  }
})

const goToPage = () => {
  router.push('/contactus')
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
